import { FontInfo } from './FontInfo';
import { FontNameAndInfo } from './FontNameAndInfo';

enum GetFontInfoResult {
  FontInfoFoundOrAlreadyLoaded,
  FontInfoMissing,
}

export class FontInfoLookup {
  public static findFirstFontAndUpdateFontsFound(
    fontNamesCommaSeparated: string,
    fontsAlreadyFoundLowerCaseRef: Set<string>
  ): FontNameAndInfo | undefined {
    if (fontNamesCommaSeparated.length <= 0) {
      return undefined;
    }

    const fontNames = fontNamesCommaSeparated.split(',');
    for (const fontName of fontNames) {
      const fontNameWithoutQuotes = this.getFontNameWithoutQuotes(fontName.trim());
      const [fontInfoResult, fontNameAndInfo] = this.tryToGetFontInfo(fontNameWithoutQuotes, fontsAlreadyFoundLowerCaseRef);
      if (fontInfoResult === GetFontInfoResult.FontInfoFoundOrAlreadyLoaded) {
        return fontNameAndInfo;
      }
    }

    return undefined;
  }

  private static tryToGetFontInfo(
    fontName: string,
    fontsAlreadyFoundLowerCaseRef: Set<string>
  ): [GetFontInfoResult, FontNameAndInfo | undefined] {
    const fontNameLowerCase = fontName.toLowerCase();
    if (fontsAlreadyFoundLowerCaseRef.has(fontNameLowerCase)) {
      // We already have loaded the font, we don't need to load font binary info again
      return [GetFontInfoResult.FontInfoFoundOrAlreadyLoaded, undefined];
    }

    const fontBase64String = this.tryToGetFontBase64String(fontNameLowerCase);
    if (!fontBase64String) {
      return [GetFontInfoResult.FontInfoMissing, undefined];
    }

    // Update fontsAlreadyFoundLowerCaseRef with new font that we just found
    fontsAlreadyFoundLowerCaseRef.add(fontNameLowerCase);
    const binaryForm = Uint8Array.from(atob(fontBase64String), (c) => c.charCodeAt(0));
    const fontNameAndInfo: FontNameAndInfo = { fontBinaryInfo: binaryForm, fontName: fontName };
    return [GetFontInfoResult.FontInfoFoundOrAlreadyLoaded, fontNameAndInfo];
  }

  private static getFontNameWithoutQuotes(fontName: string): string {
    if (fontName.length < 2) {
      return fontName;
    }

    if (fontName.startsWith("'") || fontName.startsWith('"')) {
      return fontName.substr(1, fontName.length - 2);
    }

    return fontName;
  }

  private static tryToGetFontBase64String(fontNameLowerCase: string): string | undefined {
    switch (fontNameLowerCase) {
      case 'tableau bold':
        return FontInfo.getTableauBold();
      case 'tableau bold italic':
        return FontInfo.getTableauBoldItalic();
      case 'tableau book':
        return FontInfo.getTableauBook();
      case 'tableau book italic':
        return FontInfo.getTableauBookItalic();
      case 'tableau light':
        return FontInfo.getTableauLight();
      case 'tableau light italic':
        return FontInfo.getTableauLightItalic();
      case 'tableau medium':
        return FontInfo.getTableauMedium();
      case 'tableau medium italic':
        return FontInfo.getTableauMediumItalic();
      case 'tableau regular':
        return FontInfo.getTableauRegular();
      case 'tableau regular italic':
        return FontInfo.getTableauRegularItalic();
      case 'tableau semibold':
      case 'tableau semi-bold':
        return FontInfo.getTableauSemibold();
      case 'tableau semibold italic':
      case 'tableau semi-bold italic':
        return FontInfo.getTableauSemiboldItalic();
      case 'benton sans':
      case 'benton sans book':
        return FontInfo.getBentonSansBook();
      case 'benton sans italic':
      case 'benton sans book italic':
        return FontInfo.getBentonSansBook_Italic();
      case 'benton sans low-dpi':
      case 'benton sans lowdpi':
      case 'benton sans semidemi':
        return FontInfo.getBentonSansSemiDemi();
      case 'benton sans low-dpi italic':
      case 'benton sans lowdpi italic':
      case 'benton sans semidemi italic':
        return FontInfo.getBentonSansSemiDemi_Italic();
      case 'benton sans light':
        return FontInfo.getBentonSansLight();
      case 'benton sans medium':
        return FontInfo.getBentonSansMedium();
      default:
        return undefined;
    }
  }
}
